import { useEffect, useMemo, useState } from "react";
import type {
    ConsentGovernanceItem,
    ConsentGovernanceVersion,
} from "src/api/client-api/consent/useGetConsentGovernanceAdmin";

export const ROWS_PER_PAGE_OPTIONS = [10, 25, 50];

type UseConsentGovernanceTabStateInput = {
    consentItemVersions: ConsentGovernanceVersion[];
    documents: ConsentGovernanceItem[];
    latestConsentItemVersions: ConsentGovernanceVersion[];
    optIns: ConsentGovernanceItem[];
    selectedLocale: string;
};

const getItemIdFromVersion = (version: ConsentGovernanceVersion) =>
    typeof version.consentItem === "object" && version.consentItem !== null ? version.consentItem.id : undefined;

export const useConsentGovernanceTabState = ({
    consentItemVersions,
    documents,
    latestConsentItemVersions,
    optIns,
    selectedLocale,
}: UseConsentGovernanceTabStateInput) => {
    const [selectedItemIds, setSelectedItemIds] = useState<Set<number>>(() => new Set());
    const [selectedVersionIds, setSelectedVersionIds] = useState<Set<number>>(() => new Set());

    const latestVersionByItemId = useMemo(() => {
        const map = new Map<number, ConsentGovernanceVersion>();
        for (const version of latestConsentItemVersions) {
            const itemId = getItemIdFromVersion(version);
            if (itemId !== undefined && !map.has(itemId)) {
                map.set(itemId, version);
            }
        }
        return map;
    }, [latestConsentItemVersions]);

    const filteredVersions = consentItemVersions;
    const paginatedDocuments = documents;
    const paginatedOptIns = optIns;
    const paginatedVersions = filteredVersions;
    const selectedDocuments = paginatedDocuments.filter((item) => selectedItemIds.has(item.id));
    const selectedOptIns = paginatedOptIns.filter((item) => selectedItemIds.has(item.id));
    const selectedVersions = paginatedVersions.filter((version) => selectedVersionIds.has(version.id));
    const areAllVisibleDocumentsSelected =
        paginatedDocuments.length > 0 && paginatedDocuments.every((item) => selectedItemIds.has(item.id));
    const areAllVisibleOptInsSelected =
        paginatedOptIns.length > 0 && paginatedOptIns.every((item) => selectedItemIds.has(item.id));
    const areAllVisibleVersionsSelected =
        paginatedVersions.length > 0 && paginatedVersions.every((version) => selectedVersionIds.has(version.id));

    useEffect(() => {
        setSelectedItemIds(new Set());
    }, [selectedLocale]);

    useEffect(() => {
        setSelectedVersionIds(new Set());
    }, [selectedLocale]);

    const toggleSelectedItem = (itemId: number, selected: boolean) => {
        setSelectedItemIds((current) => {
            const next = new Set(current);
            if (selected) {
                next.add(itemId);
            } else {
                next.delete(itemId);
            }
            return next;
        });
    };

    const toggleSelectedVersion = (versionId: number, selected: boolean) => {
        setSelectedVersionIds((current) => {
            const next = new Set(current);
            if (selected) {
                next.add(versionId);
            } else {
                next.delete(versionId);
            }
            return next;
        });
    };

    const setVisibleItemSelection = (items: ConsentGovernanceItem[], selected: boolean) => {
        const visibleIds = items.map((item) => item.id);
        setSelectedItemIds((current) => {
            const next = new Set(current);
            for (const id of visibleIds) {
                if (selected) {
                    next.add(id);
                } else {
                    next.delete(id);
                }
            }
            return next;
        });
    };

    const setVisibleVersionSelection = (versions: ConsentGovernanceVersion[], selected: boolean) => {
        const visibleIds = versions.map((version) => version.id);
        setSelectedVersionIds((current) => {
            const next = new Set(current);
            for (const id of visibleIds) {
                if (selected) {
                    next.add(id);
                } else {
                    next.delete(id);
                }
            }
            return next;
        });
    };

    return {
        areAllVisibleDocumentsSelected,
        areAllVisibleOptInsSelected,
        areAllVisibleVersionsSelected,
        filteredVersions,
        latestVersionByItemId,
        paginatedDocuments,
        paginatedOptIns,
        paginatedVersions,
        selectedDocuments,
        selectedItemIds,
        selectedOptIns,
        selectedVersionIds,
        selectedVersions,
        setSelectedItemIds,
        setSelectedVersionIds,
        setVisibleItemSelection,
        setVisibleVersionSelection,
        toggleSelectedItem,
        toggleSelectedVersion,
    };
};
